import clsx from "clsx";
import React, { FunctionComponent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRoute } from "wouter";

import { DealerVehicleResultsRoute } from "../../../navigation/DealerRoutes";
import { useGetVehicleInfo } from "../../../queries/useGetVehicleInfo";
import { useSearchWheelsByVehicle } from "../../../queries/useSearchWheelsByVehicle";
import { LinearProgress } from "../../../ui/LinearProgress";
import { useBreakpointMatch } from "../../../util/useBreakpointMatch";
import {
  FiltersSidebar,
  useFiltersState,
} from "../../FiltersSidebar/FiltersSidebar";
import { NoData } from "../../NoData/NoData";
import { TireFilters } from "../../Tires/Results/Filters/TireFilters";
import { useTireResults } from "../../Tires/Results/useTireResults";
import { VehicleHeader } from "../../VehicleHeader/VehicleHeader";
import { Visualizer } from "../../Visualizer/Visualizer";
import { shouldRenderVisualizer } from "../../Visualizer/shouldRenderVisualizer";
import { CombosStatusbar } from "./CombosStatusbar";
import { Showcase } from "./Showcase/Showcase";

export const Combos: FunctionComponent = () => {
  const [isVehicle] = useRoute(DealerVehicleResultsRoute);
  const { data: vehicleInfo } = useGetVehicleInfo();
  const [, { hide }] = useFiltersState();
  const { t } = useTranslation();

  const { data: tires, isLoading: tiresIsLoading } = useTireResults();
  const { data: wheels, isLoading: wheelsIsLoading } =
    useSearchWheelsByVehicle();

  const hasVisualizer = shouldRenderVisualizer(
    vehicleInfo,
    wheels?.allParts.length || 0
  );

  const isMd = useBreakpointMatch("md");
  const portalRef = useRef<HTMLDivElement>(null);

  return (
    <>
      {hasVisualizer ? (
        <Visualizer
          controls={
            <FiltersSidebar>
              <TireFilters onClose={hide} />
            </FiltersSidebar>
          }
          className="mt-4"
          compact
          parts={wheels?.filteredParts || null}
          portalRef={portalRef}
          vehicleInfo={vehicleInfo || null}
        />
      ) : (
        <>
          <VehicleHeader className="mt-10 mx-4 lg:mx-0" />
          <CombosStatusbar className={clsx(isVehicle ? "md:mt-0" : "mt-4")}>
            <FiltersSidebar>
              <TireFilters onClose={hide} />
            </FiltersSidebar>
          </CombosStatusbar>
        </>
      )}
      {[
        (tiresIsLoading || wheelsIsLoading) && (
          <LinearProgress className="my-8 h-0.5" />
        ),
        tires?.filteredParts.length && wheels?.filteredParts.length && (
          <Showcase
            portalRef={isMd && hasVisualizer && portalRef}
            tires={tires}
            wheels={wheels}
          />
        ),
        !tiresIsLoading && !wheelsIsLoading && (
          <NoData key="n">{t("searchResults.noData")}</NoData>
        ),
      ].find(Boolean)}
    </>
  );
};
